import { useContext, useEffect, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { Box, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip } from "chart.js";
import { eachDayOfInterval, format, getDate, lastDayOfMonth, parseISO } from "date-fns";
import { SidebarContext } from "src/contexts/SidebarContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

//Default colors for endpoints in bar chart
const endpointColors: Record<string, string> = {
  Analyze_Fantasy_Trade: "#9B120B",
  Player_Summary: "#6A040F",
  Start_Sit: "#F48C06",
  Waivers_Analyze: "#FFBA08",
  "/functions/fantasy_league_news": "#B71C1C",
  "/player_rankings": "#D84315",
  "/functions/user_general_info": "#8C1C13",
};

//Object for translate the names of endpointLabels
const endpointLabels: Record<string, string> = {
  Player_Summary: "Player summary",
  Analyze_Fantasy_Trade: "Analyze Trade",
  Start_Sit: "Start/Sit Analysis",
  Waivers_Analyze: "Waiver Wire Analysis",
  "/functions/fantasy_league_news": "News",
  "/player_rankings": "Player Rankings",
  "/functions/user_general_info": "Consolidated League Information",
};

export function DashboardCharts() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      backgroundColor: string;
      borderRadius: number;
    }[];
    fullLabels: string[];
  }>({ labels: [], datasets: [], fullLabels: [] });
  const { stats, isDataLoaded } = useContext(SidebarContext);

  useEffect(() => {
    if (stats && stats.visualization && stats.visualization.time_series && stats.visualization.endpoint_map && stats.end_date) {
      // get the endpoint map data (numeric key === endpoint name)
      const endpointMap = stats.visualization.endpoint_map;
      // time_series its an array of timestamp
      const timeSeries = stats.visualization.time_series;
      const endDate = parseISO(stats.end_date); // End of month date
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1); //First day of the month
      // const allDays = eachDayOfInterval({ start: startDate, end: endDate }); // Every day between the first and the last day of the month until the last endpoint is used
      const allDays = eachDayOfInterval({ start: startDate, end: lastDayOfMonth(endDate) }); // Every day between the first and the last day of the month

      // create complete labels for each day of the month
      const fullLabels = allDays.map((date) => format(date, "MM/dd"));

      // Create visible labels every 7 days and on the first day
      const labels = fullLabels.map((label, index) => (index === 0 || index % 7 === 0 ? label : ""));

      // get all the endpointKeys from the endpointMap something like -> ["0", "1", "2"]
      const endpointKeys = Object.keys(endpointMap);
      // get all the endpointNames from the endpointMap something like -> ["Analyze_Fantasy_Trade", "Start_Sit", ...]
      const endpointNames = endpointKeys.map((key) => endpointMap[key]);

      // Initialize daily data for each day of the month
      const dailyData: Record<string, number>[] = allDays.map(() => ({}));

      // Fill daily data with values from the time series
      for (const entry of timeSeries) {
        const entryDate = parseISO(entry.timestamp); // Parse entry date
        const dayIndex = getDate(entryDate) - 1; // Calculate index of the day (1 = index 0)
        for (const [endpointKey, value] of Object.entries(entry.data)) {
          const endpointName = endpointMap[endpointKey]; // Get endpoint name
          dailyData[dayIndex][endpointName] = value as number; // Assign value to corresponding day
        }
      }
      // Create datasets with data for each endpoint
      const datasets = endpointNames.map((endpointName) => {
        const dataForEndpoint = allDays.map((_, i) => dailyData[i][endpointName] ?? 0); // Get values or assign 0
        const color = endpointColors[endpointName] || "#ff672d";
        const newLabelName = endpointLabels[endpointName] || endpointName; // Translated or default label

        return {
          label: newLabelName,
          data: dataForEndpoint,
          backgroundColor: color,
          borderRadius: 1,
        };
      });

      setChartData({ labels, datasets, fullLabels }); // Update chart data state
    }
  }, [stats]);

  const options = {
    plugins: {
      legend: {
        display: true,
        labels: {
          color: "#ffffff",
          font: { size: 10 },
        },
      },
      tooltip: {
        animation: {
          duration: 0,
        },
        callbacks: {
          title: (context: any) => {
            const index = context[0]?.dataIndex; // Index of the selected data
            const labels = chartData.fullLabels; // Access full tags
            const date = labels[index]; // Get date for index
            return date ? `Date: ${date}` : "Date: N/A";
          },
          label: (context: any) => {
            const label = context.dataset.label || "";
            const value = context.raw;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          color: "#ffffff",
          font: { size: 12 },
          stepSize: 1,
          precision: 0,
        },
        grid: {
          color: `${PureLightThemeCaaS.colors.alpha.gray[30]}`,
          drawTicks: false,
        },
      },
      x: {
        stacked: true,
        ticks: {
          color: "#ffffff",
          font: { size: 12 },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: (value: any, index: number) => {
            // Show visible labels every 7 days
            return chartData.labels[index];
          },
        },
        grid: {
          color: "transparent",
        },
      },
    },
  };

  console.log("stats", stats);

  return (
    <Box
      borderRadius={1}
      sx={{
        background: `${PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100]}`,
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
        padding: 0.5,
        overflow: "hidden",
        borderRadius: 0.5,
        position: "relative",
        minHeight: { lg: "275px", md: "235px", sm: "200px", xs: 175 },
        height: "100%",
      }}
    >
      {isDataLoaded ? (
        <>
          <Box display="flex" alignItems="center" justifyContent="center" position="relative" height="40px" mb={2}>
            <Typography
              variant="h6"
              fontFamily="Roboto, arial"
              color={PureLightThemeCaaS.colors.alpha.white[100]}
              sx={{
                fontSize: isMobile ? "1rem" : "1.2rem",
                position: "absolute",
              }}
            >
              Monthly Usage
            </Typography>
          </Box>
          <Bar options={options} data={chartData} height={isMobile ? 100 : 80} />
        </>
      ) : (
        <Box
          sx={{
            position: "relative",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            bgcolor: `${PureLightThemeCaaS.colors.alpha.grayBackgroundCard[100]}`,
          }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={64} disableShrink thickness={3} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
        </Box>
      )}
    </Box>
  );
}
